import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { canAccessRoute } from '../utils/role-utils';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles: Array<'super_user' | 'site_admin' | 'end_user'>;
  redirectTo?: string;
}

/**
 * Component that protects routes based on user role
 */
export default function RoleProtectedRoute({
  children,
  allowedRoles,
  redirectTo = '/',
}: RoleProtectedRouteProps) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Hook-based route protection using path
 */
export function useRouteAccess(route: string): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return canAccessRoute(user.role, route);
}
